import { useState } from 'react';
import { Hand, MousePointerClick, ZoomIn, Move } from 'lucide-react';
import { useUiStore } from '../store/uiStore';

const STEPS = [
    {
        icon: Hand,
        title: 'Point & Move',
        description: 'Move your hand — the cursor follows your index finger tip.',
        animation: 'ar-tut-wave',
    },
    {
        icon: MousePointerClick,
        title: 'Click',
        description: 'Hover over a button for 1 second to auto-click, or pinch your thumb and index finger for a quick tap.',
        animation: 'ar-tut-pinch',
    },
    {
        icon: ZoomIn,
        title: 'Zoom',
        description: 'Make a fist with your left hand, then move it up to zoom in or down to zoom out.',
        animation: 'ar-tut-zoom',
    },
    {
        icon: Move,
        title: 'Look Around',
        description: 'Turn your head left or right to pan the camera.',
        animation: 'ar-tut-look',
    },
] as const;

export default function ARTutorial() {
    const showARTutorial = useUiStore((s) => s.showARTutorial);
    const setShowARTutorial = useUiStore((s) => s.setShowARTutorial);
    const [step, setStep] = useState(0);

    if (!showARTutorial) return null;

    const current = STEPS[step];
    const Icon = current.icon;
    const isLast = step === STEPS.length - 1;

    const dismiss = () => {
        setStep(0);
        setShowARTutorial(false);
    };

    return (
        <>
            <div
                className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/70 backdrop-blur-sm"
                onClick={(e) => { if (e.target === e.currentTarget) dismiss(); }}
            >
                <div className="relative w-full max-w-md mx-4 bg-card/95 border border-border rounded-2xl p-8 shadow-2xl">
                    {/* Skip button */}
                    <button
                        onClick={dismiss}
                        className="interactable-btn absolute top-4 right-4 text-muted-foreground hover:text-foreground text-sm px-2 py-1 rounded-lg transition-colors"
                    >
                        Skip
                    </button>

                    {/* Icon area */}
                    <div className="flex justify-center mb-6 mt-2">
                        <div className={`w-24 h-24 rounded-full bg-primary/10 border-2 border-primary/30 flex items-center justify-center ar-tut-icon ${current.animation}`}>
                            <Icon className="w-12 h-12 text-primary" strokeWidth={1.5} />
                        </div>
                    </div>

                    {/* Text */}
                    <h2 className="text-xl font-bold text-foreground text-center mb-2">
                        {current.title}
                    </h2>
                    <p className="text-muted-foreground text-center text-sm leading-relaxed mb-8">
                        {current.description}
                    </p>

                    {/* Step dots */}
                    <div className="flex justify-center gap-2 mb-6">
                        {STEPS.map((_, i) => (
                            <div
                                key={i}
                                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                                    i === step
                                        ? 'bg-primary w-6'
                                        : i < step
                                            ? 'bg-primary/50'
                                            : 'bg-muted-foreground/30'
                                }`}
                            />
                        ))}
                    </div>

                    {/* Navigation */}
                    <div className="flex gap-3">
                        {step > 0 && (
                            <button
                                onClick={() => setStep(step - 1)}
                                className="interactable-btn flex-1 py-3 rounded-xl border border-border text-foreground font-medium hover:bg-accent transition-colors"
                            >
                                Back
                            </button>
                        )}
                        <button
                            onClick={isLast ? dismiss : () => setStep(step + 1)}
                            className="interactable-btn flex-1 py-3 rounded-xl bg-primary text-primary-foreground font-medium hover:bg-primary/90 transition-colors"
                        >
                            {isLast ? 'Start Lab' : 'Next'}
                        </button>
                    </div>
                </div>
            </div>

            <style>{`
                .ar-tut-icon { transition: all 0.3s ease; }
                .ar-tut-wave { animation: ar-tut-wave-kf 2s ease-in-out infinite; }
                .ar-tut-pinch { animation: ar-tut-pinch-kf 1.5s ease-in-out infinite; }
                .ar-tut-zoom { animation: ar-tut-zoom-kf 2s ease-in-out infinite; }
                .ar-tut-look { animation: ar-tut-look-kf 2.5s ease-in-out infinite; }

                @keyframes ar-tut-wave-kf {
                    0%, 100% { transform: translateX(0); }
                    25% { transform: translateX(-8px); }
                    75% { transform: translateX(8px); }
                }
                @keyframes ar-tut-pinch-kf {
                    0%, 100% { transform: scale(1); }
                    50% { transform: scale(0.88); }
                }
                @keyframes ar-tut-zoom-kf {
                    0%, 100% { transform: translateY(0) scale(1); }
                    50% { transform: translateY(-6px) scale(1.08); }
                }
                @keyframes ar-tut-look-kf {
                    0%, 100% { transform: rotate(0deg); }
                    25% { transform: rotate(-5deg); }
                    75% { transform: rotate(5deg); }
                }
            `}</style>
        </>
    );
}
